/**
 * Palette path resolution (`ui.colors.neutral-900`, etc.).
 * Aligned with content-builder/backend/param-types/color-palette/palette-resolve.js
 */

const COLOR_PALETTE_KEYS = [
  1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50, 20, 10, 5,
] as const;

function normalizeHex(hex: unknown): string | null {
  if (typeof hex !== 'string') return null;
  const t = hex.trim();
  const m = t.match(/^#?([\da-fA-F]{3}|[\da-fA-F]{6})$/);
  if (!m) return null;
  let v = m[1];
  if (v.length === 3) {
    v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
  }
  return '#' + v.toLowerCase();
}

export function normalizePalettePathSuffix(suffix: string): string {
  const s = String(suffix).trim();
  const m = s.match(/^(primary|neutral)_(\d+)$/i);
  if (m) return `${m[1].toLowerCase()}-${m[2]}`;
  return s;
}

function inferInputFromPalette(primary: Record<string, string>): string {
  const p500 = primary['500'];
  if (typeof p500 === 'string' && normalizeHex(p500)) return normalizeHex(p500)!;
  for (const k of COLOR_PALETTE_KEYS) {
    const v = primary[String(k)];
    if (typeof v === 'string' && normalizeHex(v)) return normalizeHex(v)!;
  }
  return '#000000';
}

interface ParsedColorPalette {
  input: string;
  primary: Record<string, string>;
  neutral: Record<string, string>;
  overrides?: Record<string, string>;
}

export function parseColorPaletteRaw(raw: unknown): ParsedColorPalette | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const nestedPrimary = obj['primary'];
  const nestedNeutral = obj['neutral'];
  if (
    nestedPrimary &&
    typeof nestedPrimary === 'object' &&
    !Array.isArray(nestedPrimary) &&
    nestedNeutral &&
    typeof nestedNeutral === 'object' &&
    !Array.isArray(nestedNeutral)
  ) {
    const inputRaw = obj['input'];
    const input =
      typeof inputRaw === 'string' && normalizeHex(inputRaw) ? normalizeHex(inputRaw) : null;
    const parsed: ParsedColorPalette = {
      input: input ?? inferInputFromPalette(nestedPrimary as Record<string, string>),
      primary: {},
      neutral: {},
    };
    for (const k of COLOR_PALETTE_KEYS) {
      const pk = String(k);
      const pv = (nestedPrimary as Record<string, unknown>)[pk];
      const nv = (nestedNeutral as Record<string, unknown>)[pk];
      if (typeof pv === 'string') parsed.primary[pk] = pv;
      if (typeof nv === 'string') parsed.neutral[pk] = nv;
    }
    const overrides = obj['overrides'];
    if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
      const oo: Record<string, string> = {};
      for (const [k, v] of Object.entries(overrides as Record<string, unknown>)) {
        if (typeof v === 'string') oo[k] = v;
      }
      if (Object.keys(oo).length) parsed.overrides = oo;
    }
    return parsed;
  }
  return null;
}

function flattenPaletteForVarLookup(palette: ParsedColorPalette): Record<string, string> {
  const flat: Record<string, string> = { input: palette.input };
  for (const k of COLOR_PALETTE_KEYS) {
    const pk = String(k);
    const pv = palette.primary[pk];
    const nv = palette.neutral[pk];
    if (pv) flat[`primary-${pk}`] = pv;
    if (nv) flat[`neutral-${pk}`] = nv;
  }
  if (palette.overrides) {
    for (const [k, v] of Object.entries(palette.overrides)) {
      if (typeof v === 'string') flat[k] = v;
    }
  }
  return flat;
}

export function getPaletteShadeHex(paletteValue: unknown, flatKey: string): string | undefined {
  const parsed = parseColorPaletteRaw(paletteValue);
  if (!parsed) return undefined;
  const flat = flattenPaletteForVarLookup(parsed);
  const suffix = normalizePalettePathSuffix(flatKey);
  if (flat[suffix]) return flat[suffix];
  // Custom palettes expose one ramp as `accent.500`, stored on the primary scale.
  if (/^\d+$/.test(suffix)) return flat[`primary-${suffix}`];
  return undefined;
}
